import type {
  PlaygroundDefinition,
  PlaygroundNormalizedAssertionMode,
  PlaygroundNormalizedTestAssertion,
  PlaygroundNormalizedTestConfig,
  PlaygroundNormalizedTestSide,
  PlaygroundNormalizedTestStep,
  PlaygroundScenario,
  PlaygroundTestAssertion,
  PlaygroundTestConfig,
  PlaygroundTestDefinition,
  PlaygroundTestListing,
  PlaygroundTestSide,
  PlaygroundTestStep,
  PlaygroundTestAssertionMode,
  PlaygroundTestApiResolver,
  PlaygroundTestSubjectSelector,
  PlaygroundTestTiming
} from '../types.js';
import { MAX_TIMER_MS } from '../timeouts.js';
const DEFAULT_ASSERTION_MODE: PlaygroundNormalizedAssertionMode = {
  runSequentially: true,
  failFast: true,
};

const DEFAULT_TIME_SCALE = 1;
const DEFAULT_TIMEOUT_MS = 30_000;

/** The budget of a scenario step that names none (and of a scenario with no `stepBudgetMs`). */
export const DEFAULT_STEP_BUDGET_MS = 30_000;

const TIMINGS: readonly PlaygroundTestTiming[] = ['timeline', 'sequential'];

const slugify = (value: string, fallback = 'test') => {
  const normalized = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/--+/g, '-')
    .replace(/^-|-$/g, '')
    .trim();
  return normalized.length ? normalized : fallback;
};

const ensurePositiveNumber = (value: number | undefined, label: string, fallback: number): number => {
  if (value === undefined) {
    return fallback;
  }
  if (typeof value !== 'number' || Number.isNaN(value) || value <= 0) {
    throw new Error(`Playground test configuration expected ${label} to be a positive number.`);
  }
  return value;
};

/**
 * A budget is a positive number a timer can actually wait: the runner enforces it with `setTimeout`, which
 * fires anything above `MAX_TIMER_MS` (`Infinity` included) at once — the step would be interrupted
 * immediately — so it is refused here instead.
 */
export const ensureBudgetMs = (value: number | undefined, label: string, fallback: number): number => {
  const budgetMs = ensurePositiveNumber(value, label, fallback);
  if (budgetMs > MAX_TIMER_MS) {
    throw new Error(`Playground test configuration expected ${label} to be at most ${MAX_TIMER_MS} ms (the longest a timer can wait); got ${budgetMs}.`);
  }
  return budgetMs;
};

const validateAssertions = <ApiType>(
  assertions: PlaygroundTestAssertion<ApiType>[] | undefined,
  location: string
): PlaygroundNormalizedTestAssertion<ApiType>[] => {
  if (!assertions?.length) {
    return [];
  }
  return assertions.map((assertion, index) => {
    if (!assertion || typeof assertion !== 'object') {
      throw new Error(`Playground test assertion at ${location}[${index}] must be an object.`);
    }
    if (typeof assertion.label !== 'string' || !assertion.label.trim()) {
      throw new Error(`Playground test assertion at ${location}[${index}] is missing a label.`);
    }
    if (typeof assertion.run !== 'function') {
      throw new Error(`Playground test assertion "${assertion.label}" at ${location}[${index}] must provide an async run(api, ctx) function.`);
    }
    const budgetMs = assertion.budgetMs === undefined
      ? undefined
      : ensureBudgetMs(assertion.budgetMs, `${location}[${index}].budgetMs`, DEFAULT_STEP_BUDGET_MS);
    return {
      label: assertion.label,
      run: assertion.run,
      ...(budgetMs === undefined ? {} : { budgetMs }),
    } satisfies PlaygroundNormalizedTestAssertion<ApiType>;
  });
};

/** Whether `fn` is an `async` function: its constructor is `AsyncFunction`. */
const isAsyncFn = (fn: ((...args: never[]) => unknown) | undefined): boolean => {
  if (!fn) return false;
  return fn.constructor?.name === 'AsyncFunction';
};

const validateStepSide = <ApiType>(
  side: PlaygroundTestSide<ApiType> | undefined,
  location: string
): PlaygroundNormalizedTestSide<ApiType> | undefined => {
  if (!side) {
    return undefined;
  }
  const beforeUpdate = validateAssertions(side.beforeUpdate, `${location}.beforeUpdate`);
  const afterUpdate = validateAssertions(side.afterUpdate, `${location}.afterUpdate`);
  if (isAsyncFn(side.onUpdate)) {
    throw new Error(`Playground test ${location}.onUpdate must not be async. Only synchronous updates are allowed.`);
  }
  const normalized: PlaygroundNormalizedTestSide<ApiType> = {
    beforeUpdate,
    afterUpdate,
    ...(typeof side.onUpdate === 'function' ? { onUpdate: side.onUpdate } : {}),
  };
  return normalized;
};

const resolveAssertionMode = (
  testMode: PlaygroundTestAssertionMode | undefined,
  stepMode: PlaygroundTestAssertionMode | undefined
): PlaygroundNormalizedAssertionMode => {
  const merged = {
    runSequentially: stepMode?.runSequentially ?? testMode?.runSequentially ?? DEFAULT_ASSERTION_MODE.runSequentially,
    failFast: stepMode?.failFast ?? testMode?.failFast ?? DEFAULT_ASSERTION_MODE.failFast,
  } satisfies PlaygroundNormalizedAssertionMode;
  return merged;
};

const hasAction = <ClientApi, ServerApi>(step: PlaygroundTestStep<ClientApi, ServerApi>): boolean => {
  const client = step.client;
  const server = step.server;
  const clientHas = !!(
    client && (
      (client.beforeUpdate && client.beforeUpdate.length > 0) ||
      typeof client.onUpdate === 'function' ||
      (client.afterUpdate && client.afterUpdate.length > 0)
    )
  );
  const serverHas = !!(
    server && (
      (server.beforeUpdate && server.beforeUpdate.length > 0) ||
      typeof server.onUpdate === 'function' ||
      (server.afterUpdate && server.afterUpdate.length > 0)
    )
  );
  return clientHas || serverHas;
};

const ensureSortedSteps = <ClientApi, ServerApi>(steps: PlaygroundTestStep<ClientApi, ServerApi>[], timing: PlaygroundTestTiming) => {
  if (timing === 'sequential') {
    // Sequential steps run back to back and `atTime` is ignored, so there is no clock order to check.
    return;
  }
  let previous = -Infinity;
  steps.forEach((step, index) => {
    if (typeof step.atTime !== 'number' || Number.isNaN(step.atTime)) {
      throw new Error(`Playground test step at index ${index} must provide a numeric atTime.`);
    }
    if (step.atTime <= previous) {
      throw new Error('Playground test steps must have strictly ascending atTime values.');
    }
    previous = step.atTime;
  });
};

const defaultClientSelector: PlaygroundTestSubjectSelector<any> = (ctx) => ctx.clients[0] ?? null;
const defaultServerSelector: PlaygroundTestSubjectSelector<any> = (ctx) => ctx.areas[0] ?? null;

const defaultClientResolver: PlaygroundTestApiResolver<any> = (ctx) => {
  const client = ctx.selectedClient ?? ctx.clients[0] ?? null;
  if (!client) return undefined;
  const api = (client as any)?.process?.$api;
  return api ?? client;
};

const defaultServerResolver: PlaygroundTestApiResolver<any> = (ctx) => {
  const area = ctx.selectedArea ?? ctx.areas[0] ?? null;
  if (!area) return undefined;
  const api = (area as any)?.gameProcess?.$api;
  return api ?? (area as any)?.gameProcess ?? area;
};

const coerceTestConfig = <ClientApi, ServerApi>(
  definition: PlaygroundTestDefinition<ClientApi, ServerApi>,
  fallbackLabel: string
): PlaygroundTestConfig<ClientApi, ServerApi> => {
  if (Array.isArray(definition)) {
    return {
      label: fallbackLabel,
      steps: definition,
    } satisfies PlaygroundTestConfig<ClientApi, ServerApi>;
  }
  return definition;
};

const ensureUniqueLabel = (label: string | undefined, used: Set<string>, fallbackBase: string): string => {
  const base = typeof label === 'string' && label.trim() ? label.trim() : fallbackBase;
  let candidate = base;
  let counter = 1;
  while (used.has(candidate)) {
    counter += 1;
    candidate = `${base}-${counter}`;
  }
  used.add(candidate);
  return candidate;
};

const resolveTiming = (timing: PlaygroundTestTiming | undefined, label: string): PlaygroundTestTiming => {
  if (timing === undefined) {
    return 'timeline';
  }
  if (!TIMINGS.includes(timing)) {
    throw new Error(`Playground test "${label}" has timing ${JSON.stringify(timing)}; expected 'timeline' or 'sequential'.`);
  }
  return timing;
};

const normalizeStep = <ClientApi, ServerApi>(
  step: PlaygroundTestStep<ClientApi, ServerApi>,
  index: number,
  testMode: PlaygroundTestAssertionMode | undefined,
  timing: PlaygroundTestTiming
): PlaygroundNormalizedTestStep<ClientApi, ServerApi> => {
  if (typeof step.label !== 'string' || !step.label.trim()) {
    throw new Error(`Playground test step at index ${index} is missing a label.`);
  }
  if (!hasAction(step)) {
    throw new Error(`Playground test step "${step.label}" must include at least one client/server action or assertion.`);
  }
  const assertionMode = resolveAssertionMode(testMode, step.assertionMode);
  const client = validateStepSide(step.client, `step[${index}].client`);
  const server = validateStepSide(step.server, `step[${index}].server`);
  const normalized: PlaygroundNormalizedTestStep<ClientApi, ServerApi> = {
    // Under timeline timing `ensureSortedSteps` has already proved `atTime` a number.
    atTime: timing === 'sequential' ? index : step.atTime!,
    label: step.label,
    assertionMode,
    ...(client ? { client } : {}),
    ...(server ? { server } : {}),
  };
  return normalized;
};

/**
 * The scenario form as a timeline-form test: one step per scenario step, each a single client
 * `beforeUpdate` assertion carrying the step's budget, under `timing: 'sequential'`. Lowering onto
 * whole steps (not onto one step's assertion list) keeps the per-step START mark the console
 * attribution keys on, and the progress tree, unchanged.
 */
const lowerScenario = <ClientApi, ServerApi>(
  scenario: PlaygroundScenario<ClientApi>
): PlaygroundTestConfig<ClientApi, ServerApi> => {
  if (!Array.isArray(scenario.steps) || scenario.steps.length === 0) {
    throw new Error('Playground scenario must contain at least one step.');
  }
  const stepBudgetMs = ensureBudgetMs(scenario.stepBudgetMs, 'scenario.stepBudgetMs', DEFAULT_STEP_BUDGET_MS);
  const lowered = scenario.steps.map((step, index) => {
    if (!step || typeof step !== 'object') {
      throw new Error(`Playground scenario step at index ${index} must be an object.`);
    }
    if (typeof step.label !== 'string' || !step.label.trim()) {
      throw new Error(`Playground scenario step at index ${index} is missing a label.`);
    }
    if (typeof step.run !== 'function') {
      throw new Error(`Playground scenario step "${step.label}" must provide an async run(ctx) function.`);
    }
    const budgetMs = ensureBudgetMs(step.budgetMs, `scenario.steps[${index}].budgetMs`, stepBudgetMs);
    const timelineStep: PlaygroundTestStep<ClientApi, ServerApi> = {
      label: step.label,
      client: {
        beforeUpdate: [{
          label: step.label,
          budgetMs,
          // The scenario form is context-first; the runner calls every assertion as run(api, ctx).
          run: (_api, ctx) => step.run(ctx),
        }],
      },
    };
    return { timelineStep, budgetMs };
  });
  return {
    label: scenario.label ?? 'scenario',
    steps: lowered.map((entry) => entry.timelineStep),
    timing: 'sequential',
    // The steps run back to back and each is bounded by its own budget, so the test's budget is their sum.
    timeoutMs: lowered.reduce((sum, entry) => sum + entry.budgetMs, 0),
  };
};

/**
 * Every test of a definition with its defaults applied: the scenario (when there is one) first, then the
 * timeline-form `definitions` in order.
 */
export const normalizePlaygroundTests = <ClientApi, ServerApi>(options: {
  definitions: PlaygroundTestDefinition<ClientApi, ServerApi>[];
  scenario?: PlaygroundScenario<ClientApi>;
  selectionName: string;
}): PlaygroundNormalizedTestConfig<ClientApi, ServerApi>[] => {
  const { definitions, scenario, selectionName } = options;
  // An unlabeled timeline test is numbered among the timeline tests alone, so its fallback label — and
  // the id a `--test=<label>` run targets — does not change when a scenario is added in front of it.
  const entries: Array<{ config: PlaygroundTestConfig<ClientApi, ServerApi>; fallbackLabel: string; fromScenario: boolean }> = [
    ...(scenario ? [{ config: lowerScenario<ClientApi, ServerApi>(scenario), fallbackLabel: 'scenario', fromScenario: true }] : []),
    ...definitions.map((definition, index) => {
      const fallbackLabel = `test-${index + 1}`;
      return { config: coerceTestConfig(definition, fallbackLabel), fallbackLabel, fromScenario: false };
    }),
  ];
  if (!entries.length) {
    return [];
  }
  const usedLabels = new Set<string>();
  const usedIds = new Set<string>();

  return entries.map(({ config, fallbackLabel, fromScenario }) => {
    const uniqueLabel = ensureUniqueLabel(config.label, usedLabels, fallbackLabel);
    const { virtual } = config;
    let timing: PlaygroundTestTiming;
    let normalizedSteps: PlaygroundNormalizedTestStep<ClientApi, ServerApi>[];
    let timeScale: number;
    let timeoutMs: number;
    if (virtual) {
      // A `defineSandboxTest` test arrives lowered by its host, which runs it (`virtual.run`): its steps are what the
      // progress tree shows, so they are taken as they come — equal or unsorted `atTime`s, no steps at all (its setup
      // is the test), any `timeScale`, an unenforced `timeoutMs` keep the DSL's meaning. Only each side's shape is
      // checked, as the tree reads it.
      timing = 'timeline';
      normalizedSteps = (config.steps ?? []).map((step, stepIndex) => {
        const client = validateStepSide(step.client, `step[${stepIndex}].client`);
        const server = validateStepSide(step.server, `step[${stepIndex}].server`);
        return { atTime: step.atTime ?? stepIndex, label: step.label, assertionMode: DEFAULT_ASSERTION_MODE, ...(client ? { client } : {}), ...(server ? { server } : {}) };
      });
      timeScale = config.timeScale ?? DEFAULT_TIME_SCALE;
      timeoutMs = config.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    } else {
      if (!config.steps || !Array.isArray(config.steps) || config.steps.length === 0) {
        throw new Error(`Playground test "${uniqueLabel}" must contain at least one step.`);
      }
      timing = resolveTiming(config.timing, uniqueLabel);
      ensureSortedSteps(config.steps, timing);

      normalizedSteps = config.steps.map((step, stepIndex) => {
        const normalizedStep = normalizeStep(step, stepIndex, config.assertionMode, timing);
        // Only the lowering marks a step: an authored timeline step can never claim the scenario's exemption
        // from needing a client API (see `PlaygroundNormalizedTestStep.fromScenario`).
        return fromScenario ? { ...normalizedStep, fromScenario: true as const } : normalizedStep;
      });

      timeScale = ensurePositiveNumber(config.timeScale, 'timeScale', DEFAULT_TIME_SCALE);
      timeoutMs = ensurePositiveNumber(config.timeoutMs, 'timeoutMs', DEFAULT_TIMEOUT_MS);
    }

    const clientSelector = config.clientSelector ?? defaultClientSelector;
    const serverSelector = config.serverSelector ?? defaultServerSelector;
    const resolveClientApi = config.resolveClientApi ?? defaultClientResolver;
    const resolveServerApi = config.resolveServerApi ?? defaultServerResolver;

    const labelSlugBase = slugify(uniqueLabel, fallbackLabel);
    let idCandidate = `${slugify(selectionName, 'playground')}-${labelSlugBase}`;
    if (usedIds.has(idCandidate)) {
      let attempt = 2;
      while (usedIds.has(`${idCandidate}-${attempt}`)) {
        attempt += 1;
      }
      idCandidate = `${idCandidate}-${attempt}`;
    }
    usedIds.add(idCandidate);

    const normalized: PlaygroundNormalizedTestConfig<ClientApi, ServerApi> = {
      id: idCandidate,
      label: uniqueLabel,
      steps: normalizedSteps,
      timing,
      timeScale,
      timeoutMs,
      assertionMode: resolveAssertionMode(config.assertionMode, undefined),
      clientSelector,
      serverSelector,
      resolveClientApi,
      resolveServerApi,
      ...(virtual ? { virtual } : {}),
    };

    return normalized;
  });
};

/** A normalized test as a host lists it before a run: its id and label, its timing, how many steps, its budget. */
export const toTestListing = <ClientApi, ServerApi>(test: PlaygroundNormalizedTestConfig<ClientApi, ServerApi>): PlaygroundTestListing => ({
  id: test.id,
  label: test.label,
  timing: test.timing,
  stepCount: test.steps.length,
  timeoutMs: test.timeoutMs,
});

// `any` game types: a host lists definitions of every game, as `LoadedPlaygroundDefinition` holds them.
/**
 * A definition's tests in run order — the scenario first — with the ids and labels a mount of it gives
 * (`PlaygroundMount.listTests`), without booting it: what a host lists before a run. Its tests are read as
 * `selectPlaygroundRuntime` reads them (`tests`, else the lone `test`).
 */
export const listPlaygroundTests = (definition: PlaygroundDefinition<any, any, any>, playgroundName: string): PlaygroundTestListing[] =>
  normalizePlaygroundTests({
    definitions: definition.tests ? definition.tests : definition.test ? [definition.test] : [],
    scenario: definition.scenario,
    selectionName: playgroundName,
  }).map(toTestListing);
